import React, { useState, useMemo, useCallback, useEffect } from "react";
import { FaUser, FaList, FaHeart } from "react-icons/fa";
import { UnifiedHeader, UnifiedLegend } from "../styles/DimensionStyles";
import { deriveAnchoredAge } from '../utils/levelCalculations';
import { calculateFormProgress } from '../utils/gradeCalculationForWithdrawal';

// Definimos las interfaces localmente
interface Skill {
  id: number;
  name: string;
  levels: string[];
  selectedLevelIndex: number | null;
}

interface Dimension {
  id: number;
  name: string;
  skills: Skill[];
}

interface Student {
  id: number | string;
  name: string;
  course: string;
  birthDate: string;
  skillLevels: SkillLevel[];
  previousYearLevels?: {
    dimensionId: number;
    skillId: number;
    level: number | null;
    year: string;
  }[];
  qualitativeDescription?: string;
}

interface SkillLevel {
  dimensionId: number;
  skillId: number;
  level: number | null;
  notEvaluated?: boolean;
}

const LEVEL_COLORS = {
  expected: "bg-yellow-50 border-2 border-yellow-400", // Color amarillo claro para nivel esperado
  previous: "bg-gray-50 border-2 border-gray-400", // Color gris claro para nivel del año anterior
  selected: "bg-blue-500 text-white shadow-md", // Color azul para nivel seleccionado
  default: "bg-white hover:bg-blue-50 border border-gray-200", // Color por defecto
  notApplicable: "bg-gray-50 text-gray-400 italic cursor-pointer", // Color para "No aplica" no seleccionado
  notApplicableSelected: "bg-blue-500 text-white shadow-md italic", // Color para "No aplica" seleccionado
  previouslySelected: "bg-blue-50 border border-blue-300", // Color para nivel previamente seleccionado
};

// Definición de la dimensión y habilidades
export const amorDiosDimension: Dimension = {
  id: 11,
  name: "Amor a Dios y sentido Cristiano",
  skills: [
    {
      id: 1,
      name: "Disposición para la oración y el silencio",
      levels: [
        "Con la guía del adulto, intenta permanecer en silencio en la oración y muestra disposición a participar.",
        "Con la guía del adulto, permanece en silencio en la oración y muestra disposición a participar.",
        "Se dispone en silencio de manera más autónoma en la oración y comprende que es un momento especial de encuentro con Dios.",
        "Se dispone en silencio de manera autónoma para orar y comprende que es un momento especial de encuentro con Dios.",
        "Hace silencio de manera intencionada para orar y reflexionar, reconociendo su valor en la vida espiritual.",
        "En proceso",
        "Hace silencio de manera intencionada para orar y reflexionar, reconociendo su valor en la vida espiritual pudiendo orar de diversas formas.",
        "En proceso",
        "Mantiene el silencio como parte de su experiencia de oración y lo refleja en su postura y actitud.",
      ],
      selectedLevelIndex: null,
    },
    {
      id: 2,
      name: "Participación en instancias de oración",
      levels: [
        "Participa de instancias de oración a traves del canto, peticiones, acciones de gracias con el apoyo del adulto",
        "Participa de instancias de oración a traves del canto, peticiones, acciones de gracias de manera espontanea y en ocasiones con el apoyo del adulto",
        "Participa de instancias de oración a través del canto, peticiones y acciones de gracias espontáneamente.",
        "Participa de instancias de oración a través del canto, peticiones, acciones de gracias y recitando oraciones universales con ayuda del adulto.",
        "Participa de instancias de oración a través del canto, peticiones, acciones de gracias y recitando oraciones universales.",
        "En proceso",
        "Participa activamente en la oración, expresando su fe de manera reflexiva y respetuosa.",
        "En proceso",
        "Manifiesta su compromiso con la oración, invitando a otros a participar y fomentando un ambiente de recogimiento.",
      ],
      selectedLevelIndex: null,
    },
    {
      id: 3,
      name: "Reconocimiento del amor de Dios",
      levels: [
        "Expresa sentirse querido por su núcleo familiar, manifestándolo en palabras o dibujos.",
        "Expresa sentirse querido por quienes lo rodean, manifestándolo en palabras o dibujos.",
        "Con apoyo del adulto reconoce la presencia del amor de Dios a través de quienes lo cuidan, y en otros de su entorno más cercano verbalizándolo o dibujándolo.",
        "En proceso",
        "Reconoce la presencia del amor de Dios a través de quienes lo cuidan, y en otros de su entorno más cercano verbalizándolo o dibujandolo.",
        "En proceso",
        "Identifica y valora el amor de Dios en su vida cotidiana, expresándolo espontáneamente.",
        "En proceso",
        "Manifiesta su fe en el amor de Dios a través de sus acciones y relaciones con los demás.",
      ],
      selectedLevelIndex: null,
    },
    {
      id: 4,
      name: "Práctica de valores cristianos",
      levels: [
        "Con ayuda del adulto, identifica gestos de bondad y respeto en su entorno.",
        "Con ayuda del adulto, identifica y practica gestos de bondad y respeto en su entorno.",
        "Identifica y practica gestos de bondad y respeto en su entorno.",
        "Con ayuda del adulto, identifica y practica gestos de bondad y respeto en su entorno.",
        "Expresa con palabras y acciones valores cristianos como la solidaridad, la gratitud y el perdón en situaciones cotidianas.",
        "En proceso",
        "Aplica valores cristianos en sus relaciones interpersonales, promoviendo el respeto mutuo.",
        "En proceso",
        "Actúa con compromiso cristiano en su entorno, promoviendo el respeto y la solidaridad.",
      ],
      selectedLevelIndex: null,
    },
    {
      id: 5,
      name: "Conocimiento de las enseñanzas de Jesús",
      levels: [
        "Escucha relatos de la vida de Jesús, mostrándo interés con ayuda del adulto.",
        "Escucha relatos de la vida de Jesús, mostrándo interés.",
        "Reconoce y escucha relatos de la vida de Jesús, mostrando interés.",
        "Identifica enseñanzas de Jesús y empieza a relacionarlas con su vida cotidiana, con ayuda del adulto.",
        "Identifica enseñanzas de Jesús y empieza a relacionarlas con su vida cotidiana.",
        "En proceso",
        "Comprende el significado de las enseñanzas de Jesús y los sacramentos en la vida cristiana.",
        "En proceso",
        "Aplica las enseñanzas de Jesús en su vida diaria a través de las actividades formativas propuestas por el colegio.",
      ],
      selectedLevelIndex: null,
    },
    {
      id: 6,
      name: "Reconocimiento de espacios sagrados",
      levels: [
        "Con la guía del adulto, reconoce lugares como el atrio, la capilla del colegio, el altar en la sala de clases como especiales para encontrarse con Dios.",
        "En proceso",
        "Reconoce lugares como el atrio, la capilla del colegio, el altar de la sala o un espacio de oración como especiales para encontrarse con Dios.",
        "Identifica espacios sagrados y objetos religiosos que favorecen la oración y el recogimiento, con ayuda del adulto.",
        "Identifica de manera más autónoma espacios sagrados y objetos religiosos que favorecen la oración y el recogimiento.",
        "En proceso",
        "Comprende el valor de los espacios de oración y reconoce en su vida cotidiana momentos y lugares donde puede fortalecer su conexión con Dios.",
        "En proceso",
        "Busca y valora activamente espacios y momentos de recogimiento y oración, comprendiendo su importancia en la vida espiritual.",
      ],
      selectedLevelIndex: null,
    },
  ],
};

const AmorDios: React.FC<{
  students: Student[];
  selectedStudentIds: (number | string)[];
  handleLevelChange: (
    studentId: number | string,
    dimensionId: number,
    skillId: number,
    levelIndex: number
  ) => void;
  handleNotEvaluatedChange?: (studentId: number | string, dimensionId: number, skillId: number, notEvaluated: boolean) => void;
  viewMode: "student" | "skill";
  handleViewModeChange: (mode: "student" | "skill") => void;
  showInstructions: boolean;
  selectedCourse: string;
  setSelectedCourse: (course: string) => void;
  courses: string[];
  handleStudentSelect: (
    event: React.MouseEvent<HTMLButtonElement>,
    id: number | string
  ) => void;
  getExpectedLevel: (age: number) => number;
  getPreviousYearLevel: (age: number) => number;
}> = ({
  students,
  selectedStudentIds,
  handleLevelChange,
  handleNotEvaluatedChange,
  viewMode,
  handleViewModeChange,
  showInstructions,
  selectedCourse,
  setSelectedCourse,
  courses,
  handleStudentSelect,
  getExpectedLevel,
  getPreviousYearLevel,
}) => {
  const [selectedSkillId, setSelectedSkillId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "lastName" | "course">("lastName");
  const [showAllYears, setShowAllYears] = useState(true);

  // Función para extraer el primer apellido
  // Formato esperado: "Apellido Paterno; Apellido Materno; Nombre"
  const getFirstLastName = (fullName: string): string => {
    const nameParts = fullName.trim().split(' ');
    // El primer apellido (apellido paterno) es la primera parte del nombre
    if (nameParts.length >= 1) {
      return nameParts[0].toLowerCase();
    }
    // Si está vacío, retornamos la cadena vacía
    return '';
  };

  // Función para obtener el color según el porcentaje de completado
  const getCompletionColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      return 'bg-indigo-500 text-white border-indigo-600 shadow-md transform scale-105';
    }
    
    if (percentage >= 75) {
      return 'bg-green-50 text-green-800 border-green-300 hover:bg-green-100 hover:border-green-400';
    } else if (percentage >= 50) {
      return 'bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100 hover:border-blue-400';
    } else if (percentage >= 25) {
      return 'bg-yellow-50 text-yellow-800 border-yellow-300 hover:bg-yellow-100 hover:border-yellow-400';
    } else {
      return 'bg-red-50 text-red-800 border-red-300 hover:bg-red-100 hover:border-red-400';
    }
  };

  // Función para obtener el color del texto del porcentaje
  const getPercentageTextColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      return 'text-white opacity-75';
    }
    
    if (percentage >= 75) {
      return 'text-green-600 opacity-90';
    } else if (percentage >= 50) {
      return 'text-blue-600 opacity-90';
    } else if (percentage >= 25) {
      return 'text-yellow-600 opacity-90';
    } else {
      return 'text-red-600 opacity-90';
    }
  };

  const getSkillLevel = useCallback(
    (student: Student, dimensionId: number, skillId: number): number | null => {
      const skillLevel = student.skillLevels.find(
        (sl) => sl.dimensionId === dimensionId && sl.skillId === skillId
      );
      return skillLevel?.level ?? null;
    },
    []
  );

  // 🚫 Función para verificar si una habilidad está marcada como "No evaluada"
  const isSkillNotEvaluated = (student: Student, dimensionId: number, skillId: number): boolean => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.notEvaluated === true;
  };

  // 🎯 NUEVO: Helper compartido para obtener el nivel del año anterior REAL
  const getHistoricalLevel = useCallback((student: Student, skillId: number, currentAge: number) => {
    if (student.previousYearLevels && student.previousYearLevels.length > 0) {
      const historical = student.previousYearLevels.find(
        pl => pl.dimensionId === amorDiosDimension.id && pl.skillId === skillId
      );
      if (historical !== undefined && historical.level !== null) {
        return historical.level;
      }
    }
    return getPreviousYearLevel(currentAge);
  }, []);

  useEffect(() => {
    if (viewMode === "student" && selectedStudentIds.length === 0 && students.length > 0) {
      // Usar el primer estudiante disponible del curso actual, no hardcodear
      const firstStudent = students[0];
      if (firstStudent) {
        handleStudentSelect(
          new MouseEvent("click") as any,
          firstStudent.id as any
        );
      }
    } else if (viewMode === "skill" && !selectedSkillId) {
      const firstSkill = amorDiosDimension.skills[0];
      if (firstSkill) {
        setSelectedSkillId(firstSkill.id);
      }
    }
  }, [
    viewMode,
    students,
    selectedStudentIds,
    handleStudentSelect,
    selectedSkillId,
  ]);

  // Función para obtener los años visibles basados en la edad del estudiante
  const getVisibleYears = useCallback(
    (studentAge: number) => {
      if (showAllYears) {
        return Array.from({ length: 9 }, (_, i) => i + 4); // Años 4-12
      } else {
        // Mostrar edad del estudiante ± 2 años, limitado a 4-12
        const minYear = Math.max(4, studentAge - 2);
        const maxYear = Math.min(12, studentAge + 2);
        const years = [];
        for (let year = minYear; year <= maxYear; year++) {
          years.push(year);
        }
        return years;
      }
    },
    [showAllYears]
  );

  const handleLocalLevelChange = useCallback(
    (studentId: number | string, skillId: number, levelIndex: number | null) => {
      const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
      const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
      if (student) {
        const currentLevel = getSkillLevel(student, amorDiosDimension.id, skillId);

        // Lógica de toggle: si el nivel ya está seleccionado, deseleccionarlo
        if (currentLevel === levelIndex) {
          handleLevelChange(studentId, amorDiosDimension.id, skillId, null);
          return;
        }
      }

      // Si levelIndex es -1, convertir a null (para compatibilidad con el botón de reiniciar)
      const actualLevelIndex = levelIndex === -1 ? null : levelIndex;
      handleLevelChange(studentId, amorDiosDimension.id, skillId, actualLevelIndex);
    },
    [handleLevelChange, students, getSkillLevel]
  );

  // Filtrar estudiantes basados en el término de búsqueda y ordenarlos
  const filteredStudents = useMemo(() => {
    const filtered = students.filter(student => 
      selectedCourse ? student.course === selectedCourse : true
    );
    
    let sorted = [...filtered];
    
    // Ordenar según el criterio seleccionado
    if (sortBy === "name") {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "lastName") {
      sorted.sort((a, b) => {
        const lastNameA = getFirstLastName(a.name);
        const lastNameB = getFirstLastName(b.name);
        return lastNameA.localeCompare(lastNameB);
      });
    } else if (sortBy === "course") {
      sorted.sort((a, b) => a.course.localeCompare(b.course));
    }
    
    if (!searchTerm.trim()) return sorted;
    
    return sorted.filter(student => 
      student.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [students, selectedCourse, searchTerm, sortBy]);

  const renderStudentView = useMemo(() => {
    return () =>
      selectedStudentIds.map((studentId) => {
        const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
        const student = students.find((s) => normalizeId(s.id) === normalizeId(studentId));
        if (!student) return null;

        const age = deriveAnchoredAge({ 
          course: student.course, 
          birthDate: student.birthDate, 
          preferCourse: true 
        });

        const expectedLevel = getExpectedLevel(age);

        const allYears = Array.from(
          { length: 9 },
          (_, i) => `Hacia los ${i + 4} años`
        );

        const getVisibleYears = () => {
          if (showAllYears) {
            return allYears;
          }

          const studentAge = age;
          const minAge = Math.max(4, studentAge - 2);
          const maxAge = Math.min(12, studentAge + 2);

          return allYears.filter((_, index) => {
            const yearAge = index + 4;
            return yearAge >= minAge && yearAge <= maxAge;
          });
        };

        const visibleYears = getVisibleYears();
        const visibleIndices = visibleYears.map((year, index) => {
          const match = year.match(/\d+/);
          const yearNum = match ? parseInt(match[0]) : 4;
          const calculatedIndex = yearNum - 4;
          // Asegurar que nunca sea NaN y usar fallback con index
          return isNaN(calculatedIndex) ? index : calculatedIndex;
        });

        return (
          <div key={student.id} className="mb-8">
            <div className="overflow-x-auto">
              <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
                <div className="bg-gray-50 p-3 border-b border-gray-200">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-700">
                        Progreso de habilidades:
                      </span>
                      <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                        {(() => {
                          // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                          const progress = calculateFormProgress(
                                  amorDiosDimension.id,
                            student.course,
                            student.skillLevels
                          );
                          const progressPercent = progress.percentage;

                          let barColor = "bg-red-500";
                          if (progressPercent >= 75) barColor = "bg-green-500";
                          else if (progressPercent >= 50)
                            barColor = "bg-blue-500";
                          else if (progressPercent >= 25)
                            barColor = "bg-yellow-500";

                          return (
                            <div
                              className={`h-2.5 rounded-full ${barColor}`}
                              style={{ width: `${progressPercent}%` }}
                            ></div>
                          );
                        })()}
                      </div>
                      <span className="text-xs text-gray-500">
                        {(() => {
                          const progress = calculateFormProgress(
                                amorDiosDimension.id,
                            student.course,
                            student.skillLevels
                          );
                          return `${progress.completedSkills} de ${progress.totalValidSkills} habilidades`;
                        })()}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowAllYears(!showAllYears)}
                        className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                          showAllYears
                            ? "bg-blue-500 text-white border-blue-500 hover:bg-blue-600"
                            : "bg-white text-blue-500 border-blue-500 hover:bg-blue-50"
                        }`}
                      >
                        {showAllYears
                          ? "Filtrar por edad (±2 años)"
                          : "Mostrar todos los años (4-12)"}
                      </button>

                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              "¿Seguro que desea reiniciar todos los niveles de habilidad para este estudiante?"
                            )
                          ) {
                            amorDiosDimension.skills.forEach((skill) => {
                              handleLocalLevelChange(
                                student.id as any,
                                skill.id,
                                -1
                              );
                            });
                          }
                        }}
                        className="text-xs text-red-500 hover:text-red-700 font-medium bg-white px-2 py-1 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                      >
                        Reiniciar
                      </button>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                      <tr>
                        <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">
                          Habilidad
                        </th>
                        {visibleYears.map((ageGroup, index) => {
                          const actualIndex = visibleIndices[index];
                          return (
                            <th key={`student-${student.id}-item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                              <div className="flex flex-col items-center">
                                <span className="mb-1 font-bold">{ageGroup}</span>
                                <button
                                  onClick={() => {
                                    amorDiosDimension.skills.forEach(
                                      (skill) => {
                                        const currentLevel = getSkillLevel(
                                          student,
                                          amorDiosDimension.id,
                                          skill.id
                                        );
                                        if (currentLevel === actualIndex) {
                                          handleLocalLevelChange(
                                            student.id as any,
                                            skill.id,
                                            null
                                          );
                                        } else {
                                          handleLocalLevelChange(
                                            student.id as any,
                                            skill.id,
                                            actualIndex
                                          );
                                        }
                                      }
                                    );
                                  }}
                                  className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                                >
                                  Seleccionar todo
                                </button>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {amorDiosDimension.skills.map((skill, skillIndex) => {
                        const currentLevel = getSkillLevel(
                          student,
                          amorDiosDimension.id,
                          skill.id
                        );

                        const previousYearLevel = getHistoricalLevel(student, skill.id, age);

                        return (
                          <tr key={skill.id} className={`${skillIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${isSkillNotEvaluated(student, amorDiosDimension.id, skill.id) ? "opacity-60" : ""}`}>
                            <td className="py-3 px-6 text-sm font-medium text-gray-900">
                              <div className="flex items-start">
                                <span className="rounded-full bg-indigo-100 text-indigo-800 w-6 h-6 flex items-center justify-center mr-2 font-bold text-xs">
                                  {skillIndex + 1}
                                </span>
                                <div className="flex-1">
                                  {skill.name}
                                  {currentLevel !== null && !isSkillNotEvaluated(student, amorDiosDimension.id, skill.id) && (
                                    <div className="mt-1 text-xs text-green-600 font-medium">
                                      Nivel seleccionado: {currentLevel + 1}
                                    </div>
                                  )}
                                  {isSkillNotEvaluated(student, amorDiosDimension.id, skill.id) && (
                                    <div className="mt-1 text-xs text-orange-600 font-medium">
                                      🚫 No evaluada
                                    </div>
                                  )}
                                  {/* Botón para marcar como No Evaluada */}
                                  {handleNotEvaluatedChange && (
                                    <div className="mt-2">
                                      <button
                                        onClick={() => handleNotEvaluatedChange(student.id, amorDiosDimension.id, skill.id, !isSkillNotEvaluated(student, amorDiosDimension.id, skill.id))}
                                        className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                          isSkillNotEvaluated(student, amorDiosDimension.id, skill.id)
                                            ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                            : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                        }`}
                                        title={isSkillNotEvaluated(student, amorDiosDimension.id, skill.id) ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                      >
                                        {isSkillNotEvaluated(student, amorDiosDimension.id, skill.id) ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            {visibleYears.map((ageGroup, index) => {
                              const actualIndex = visibleIndices[index];
                              const levelText =
                                skill.levels[actualIndex] || "No Aplica";
                              const isSelected = currentLevel === actualIndex;
                              const isExpected = actualIndex === expectedLevel;
                              const isPrevious =
                                actualIndex === previousYearLevel;
                              const isNotApplicable = levelText === "No Aplica";

                              let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                              
                              // 1. Fondo según selección o estado base
                              if (isNotApplicable) {
                                if (isSelected) {
                                  buttonClass += LEVEL_COLORS.notApplicableSelected;
                                } else {
                                  buttonClass += LEVEL_COLORS.notApplicable;
                                }
                              } else if (isSelected) {
                                buttonClass += LEVEL_COLORS.selected;
                              } else {
                                buttonClass += LEVEL_COLORS.default;
                              }

                              // 2. Bordes e indicadores adicionales (pueden coexistir con la selección)
                              let extraStyles: React.CSSProperties = {
                                boxSizing: 'border-box'
                              };

                              if (isPrevious) {
                                // Aplicar borde gris grueso para nivel año anterior
                                extraStyles.border = '4px solid #9CA3AF'; // Gris-400
                                if (currentLevel !== actualIndex) {
                                  extraStyles.backgroundColor = '#F9FAFB'; // Gris-50 si no está seleccionado
                                }
                              }

                              if (isExpected) {
                                // Si es esperado, aplicar borde amarillo
                                if (!isPrevious) { // Evitar doble borde si es ambos
                                  extraStyles.border = '4px solid #FACC15'; // Amarillo-400
                                  if (currentLevel !== actualIndex) {
                                    extraStyles.backgroundColor = '#FEFCE8'; // Amarillo-50
                                  }
                                }
                              }

                              return (
                                <td
                                  key={`student-${student.id}-item-${ageGroup}-${index}`}
                                  className="p-1"
                                  style={{
                                    position: 'relative',
                                    zIndex: 1
                                  }}
                                >
                                  <div
                                    onClick={(e) => {
                                      console.log('🔍 Div AmordeDios clickeado:', {
                                        levelText,
                                        levelIndex: actualIndex,
                                        isNotApplicable,
                                        studentId: student.id,
                                        skillId: skill.id,
                                        skillLevelsLength: skill.levels.length,
                                        eventType: e.type,
                                        target: e.target,
                                        targetTagName: (e.target as HTMLElement).tagName,
                                        currentTarget: e.currentTarget
                                      });

                                      // Verificar que levelIndex sea válido para esta habilidad
                                      if (actualIndex >= skill.levels.length) {
                                        console.log('❌ levelIndex fuera de rango:', actualIndex, '>=', skill.levels.length);
                                        return;
                                      }

                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('🔍 AmordeDios - Calling handleLevelChange with:', {
                                        studentId: student.id,
                                        studentIdType: typeof student.id,
                                        dimensionId: amorDiosDimension.id,
                                        skillId: skill.id,
                                        levelIndex: actualIndex
                                      });
                                      handleLocalLevelChange(
                                        student.id,
                                        skill.id,
                                        actualIndex
                                      );
                                    }}
                                    onMouseDown={(e) => {
                                      console.log('🔍 MouseDown en div AmordeDios:', {
                                        levelText,
                                        levelIndex: actualIndex,
                                        isNotApplicable,
                                        button: e.button,
                                        target: (e.target as HTMLElement).tagName
                                      });
                                    }}
                                    className={`${buttonClass} cursor-pointer`}
                                    style={{
                                      ...extraStyles,
                                      pointerEvents: 'auto',
                                      userSelect: 'none',
                                      width: '100%',
                                      height: '100%',
                                      minHeight: '60px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      position: 'relative',
                                      zIndex: 2
                                    }}
                                    title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${actualIndex + 1}: ${levelText}${isPrevious ? ' (Nivel año anterior)' : ''}${isExpected ? ' (Nivel esperado)' : ''}`}
                                  >
                                    {levelText}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        );
      });
  }, [
    selectedStudentIds,
    students,
    showAllYears,
    getExpectedLevel,
    getPreviousYearLevel,
    getSkillLevel,
    handleLevelChange,
    handleLocalLevelChange,
  ]);

  const renderSkillView = useMemo(() => {
    return () => {
      if (!selectedSkillId)
        return (
          <div className="text-center py-8 text-gray-500">
            Selecciona una habilidad para comenzar
          </div>
        );

      const skill = amorDiosDimension.skills.find(
        (s) => s.id === selectedSkillId
      );
      if (!skill)
        return (
          <div className="text-center py-8 text-gray-500">
            Habilidad no encontrada
          </div>
        );

      // Generar array completo de años (4-12)
      const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
      
      // Filtrar años según la configuración 
      const getVisibleYears = () => {
        if (showAllYears) {
          return allYears;
        }
        
        // Para vista por habilidad, mostrar un rango fijo de ±2 años
        // calculado basado en la edad promedio de los estudiantes filtrados
        const averageAge = filteredStudents.length > 0 ? 
          filteredStudents.reduce((sum, student) => {
            const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
            return sum + age;
          }, 0) / filteredStudents.length : 6;
        
        const minAge = Math.max(4, Math.floor(averageAge) - 2);
        const maxAge = Math.min(12, Math.floor(averageAge) + 2);
        
        return allYears.filter((_, index) => {
          const yearAge = index + 4;
          return yearAge >= minAge && yearAge <= maxAge;
        });
      };
      
      const visibleYears = getVisibleYears();
      const visibleIndices = visibleYears.map((year, index) => {
        const match = year.match(/\d+/);
        const yearNum = match ? parseInt(match[0]) : 4;
        const calculatedIndex = yearNum - 4;
        // Asegurar que nunca sea NaN y usar fallback con index
        return isNaN(calculatedIndex) ? index : calculatedIndex;
      });

      // Estado masivo de "No evaluada" para esta habilidad en los estudiantes filtrados
      const allStudentsNotEvaluated =
        filteredStudents.length > 0 &&
        filteredStudents.every((student) =>
          isSkillNotEvaluated(student, amorDiosDimension.id, skill.id)
        );

      const toggleNotEvaluatedForAll = () => {
        if (!handleNotEvaluatedChange) return;
        const nextState = !allStudentsNotEvaluated;
        filteredStudents.forEach((student) => {
          handleNotEvaluatedChange(
            student.id,
            amorDiosDimension.id,
            selectedSkillId,
            nextState
          );
        });
      };

      return (
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gray-50 p-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">
                  {skill.name}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowAllYears(!showAllYears)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                      showAllYears
                        ? "bg-blue-500 text-white hover:bg-blue-600"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {showAllYears
                      ? "Filtrar por edad (±2 años)"
                      : "Mostrar todos los años (4-12)"}
                  </button>
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          "¿Seguro que desea reiniciar todos los niveles de esta habilidad para todos los estudiantes?"
                        )
                      ) {
                        filteredStudents.forEach((student) => {
                          handleLocalLevelChange(
                            student.id as any,
                            skill.id,
                            -1
                          );
                        });
                      }
                    }}
                    className="text-xs text-red-500 hover:text-red-700 font-medium bg-white px-2 py-1 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                  >
                    Reiniciar todo
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">
                      <div className="flex flex-col items-start gap-1">
                        <span>Estudiante</span>
                        {handleNotEvaluatedChange && (
                          <button
                            onClick={toggleNotEvaluatedForAll}
                            className={`px-3 py-1.5 rounded text-[11px] font-semibold transition-all border ${
                              allStudentsNotEvaluated
                                ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                : "bg-white text-indigo-900 border-white/50 hover:bg-indigo-50"
                            }`}
                            title="Marcar esta habilidad como no evaluada para todos los estudiantes filtrados"
                          >
                            {allStudentsNotEvaluated
                              ? "✓ Habilitar evaluación"
                              : "🚫 No evaluar esta habilidad"}
                          </button>
                        )}
                      </div>
                    </th>
                    {visibleYears.map((ageGroup, index) => {
                      const actualIndex = visibleIndices[index];
                      return (
                        <th
                          key={`item-${ageGroup}-${index}`}
                          className="py-3 px-4 text-center text-xs font-medium uppercase tracking-wider"
                        >
                          <div className="flex flex-col items-center">
                            <span className="mb-1 text-white">
                              {ageGroup}
                            </span>
                            <button
                              onClick={() => {
                                filteredStudents.forEach((student) => {
                                  const currentLevel = getSkillLevel(
                                    student,
                                    amorDiosDimension.id,
                                    selectedSkillId
                                  );
                                  if (currentLevel === actualIndex) {
                                    handleLocalLevelChange(
                                      student.id,
                                      selectedSkillId,
                                      null
                                    );
                                  } else {
                                    handleLocalLevelChange(
                                      student.id,
                                      selectedSkillId,
                                      actualIndex
                                    );
                                  }
                                });
                              }}
                              className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all uppercase tracking-wider"
                            >
                              Seleccionar todo
                            </button>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map((student) => {
                    const age = deriveAnchoredAge({ 
                      course: student.course, 
                      birthDate: student.birthDate, 
                      preferCourse: true 
                    });

                    const expectedLevel = getExpectedLevel(age);
                    
                    const previousYearLevel = getHistoricalLevel(student, skill.id, age);

                    const currentLevel = getSkillLevel(
                      student,
                      amorDiosDimension.id,
                      skill.id
                    );

                    const skillIsNotEvaluated = isSkillNotEvaluated(student, amorDiosDimension.id, skill.id);

                    return (
                      <tr key={student.id} className={`hover:bg-gray-50 ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                        <td className="py-4 px-6 whitespace-nowrap">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-700">
                                  {student.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .substring(0, 2)}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4 flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {student.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {student.course}
                              </div>
                              {skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-orange-600 font-medium">
                                  🚫 No evaluada
                                </div>
                              )}
                              {/* Botón para marcar como No Evaluada */}
                              {handleNotEvaluatedChange && (
                                <div className="mt-2">
                                  <button
                                    onClick={() => handleNotEvaluatedChange(student.id, amorDiosDimension.id, skill.id, !skillIsNotEvaluated)}
                                    className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                      skillIsNotEvaluated
                                        ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                        : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                    }`}
                                    title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                  >
                                    {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {visibleIndices.map((levelIndex, mapIndex) => {
                          const levelText = skill.levels[levelIndex] || "No Aplica";
                          const isSelected = currentLevel === levelIndex;
                          const isExpected = levelIndex === expectedLevel;
                          const isPrevious = levelIndex === previousYearLevel;
                          const isNotApplicable = levelText === "No Aplica";

                          let cellClass = "py-4 px-4 text-center cursor-pointer transition-all duration-200 ";

                          // 1. Fondo según selección o estado base
                          if (isSelected) {
                            cellClass += isNotApplicable ? LEVEL_COLORS.notApplicableSelected : LEVEL_COLORS.selected;
                          } else if (isNotApplicable) {
                            cellClass += LEVEL_COLORS.notApplicable;
                          } else {
                            cellClass += LEVEL_COLORS.default;
                          }

                          // 2. Bordes e indicadores adicionales (pueden coexistir con la selección)
                          let extraStyles: React.CSSProperties = {
                            pointerEvents: 'auto',
                            userSelect: 'none',
                            width: '100%',
                            height: '100%',
                            minHeight: '60px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            zIndex: 2,
                            boxSizing: 'border-box'
                          };

                          if (isPrevious) {
                            // Aplicar borde gris grueso para nivel año anterior
                            extraStyles.border = '4px solid #9CA3AF'; // Gris-400
                            extraStyles.backgroundColor = isSelected ? undefined : '#F9FAFB'; // Gris-50 si no está seleccionado
                          }

                          if (isExpected) {
                            // Si es esperado, aplicar borde amarillo (si es previo, el previo gana o se combinan)
                            if (!isPrevious) {
                              extraStyles.border = '4px solid #FACC15'; // Amarillo-400
                              extraStyles.backgroundColor = isSelected ? undefined : '#FEFCE8'; // Amarillo-50
                            } else {
                              // Si es AMBOS (previo y esperado), borde gris pero quizá un indicador interno
                              // Por ahora dejamos el previo como prioridad de borde
                            }
                          }

                          return (
                            <td
                              key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`}
                              className="py-2 px-2 text-center"
                              style={{
                                position: 'relative',
                                zIndex: 1
                              }}
                            >
                              <div
                                onClick={(e) => {
                                  console.log('🔍 Div AmordeDios clickeado (segunda ubicación):', {
                                    levelText,
                                    levelIndex,
                                    isNotApplicable,
                                    studentId: student.id,
                                    skillId: skill.id,
                                    skillLevelsLength: skill.levels.length,
                                    eventType: e.type,
                                    target: e.target,
                                    targetTagName: (e.target as HTMLElement).tagName,
                                    currentTarget: e.currentTarget
                                  });

                                  // Verificar que levelIndex sea válido para esta habilidad
                                  if (levelIndex >= skill.levels.length) {
                                    console.log('❌ levelIndex fuera de rango (segunda ubicación):', levelIndex, '>=', skill.levels.length);
                                    return;
                                  }

                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleLocalLevelChange(
                                    student.id,
                                    skill.id,
                                    levelIndex
                                  );
                                }}
                                onMouseDown={(e) => {
                                  console.log('🔍 MouseDown en div AmordeDios (segunda ubicación):', {
                                    levelText,
                                    levelIndex,
                                    isNotApplicable,
                                    button: e.button,
                                    target: (e.target as HTMLElement).tagName
                                  });
                                }}
                                className={`${cellClass} cursor-pointer`}
                                style={extraStyles}
                                title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${levelText}${isPrevious ? ' (Nivel año anterior)' : ''}${isExpected ? ' (Nivel esperado)' : ''}`}
                              >
                                <span className="text-center text-xs">{levelText}</span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    };
  }, [
    selectedSkillId,
    filteredStudents,
    showAllYears,
    getVisibleYears,
    getExpectedLevel,
    getPreviousYearLevel,
    getSkillLevel,
    handleLevelChange,
    handleLocalLevelChange,
  ]);

  const renderSelectionButtons = () => {
    if (viewMode === "student") {
      return (
        <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="text-lg font-semibold whitespace-nowrap">
              Seleccionar Estudiante:
            </h3>

            <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
              {/* Barra de búsqueda */}
              <div className="relative w-full md:w-96 mx-auto">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    viewBox="0 0 20 20"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Buscar estudiante..."
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                )}
              </div>

              {/* Selector de ordenamiento */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">
                  Ordenar por:
                </label>
                <select
                  value={sortBy}
                  onChange={(e) =>
                    setSortBy(e.target.value as "name" | "lastName" | "course")
                  }
                  className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="lastName">Apellido</option>
                  <option value="course">Curso</option>
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {filteredStudents.length === 0 ? (
              <div className="col-span-full text-center py-4 text-gray-500">
                No se encontraron estudiantes con "{searchTerm}"
              </div>
            ) : (
              filteredStudents.map((student) => {
                // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
                const progress = calculateFormProgress(
                  amorDiosDimension.id,
                  student.course,
                  student.skillLevels
                );
                const percentage = progress.percentage;
                const isSelected = student.id && selectedStudentIds.some(sel => normalizeId(sel) === normalizeId(student.id));

                return (
                  <button
                    key={student.id}
                    onClick={(event) => handleStudentSelect(event, student.id as any)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                      getCompletionColor(percentage, isSelected)
                    }`}
                  >
                    <div
                      className="font-semibold"
                      title={`${student.name} - ${student.course}`}
                    >
                      {student.name}
                    </div>
                    <div className={`text-xs font-medium ${getPercentageTextColor(percentage, isSelected)}`}>
                      {percentage.toFixed(1)}% completado
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center flex-wrap">
                <p className="text-sm font-medium text-gray-700 mr-2">
                  Leyenda:
                </p>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥75% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥50% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥25% completado,</span>
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    &lt;25% completado
                  </span>
                </div>
              </div>
              <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
                Tip: Ctrl + clic para seleccionar múltiples estudiantes
              </span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
          <h3 className="text-lg font-semibold whitespace-nowrap">
            Seleccionar Habilidad:
          </h3>

          <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
            {/* Barra de búsqueda */}
            <div className="relative w-full md:w-96 mx-auto">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  viewBox="0 0 20 20"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                  />
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                placeholder="Buscar habilidad..."
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </button>
              )}
            </div>

            {/* Selector de ordenamiento */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">
                Ordenar por:
              </label>
              <select
                value={sortBy}
                onChange={(e) =>
                  setSortBy(e.target.value as "name" | "lastName" | "course")
                }
                className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="name">Nombre</option>
                <option value="lastName">Número</option>
                <option value="course">% Completado</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {amorDiosDimension.skills
            .filter(
              (skill) =>
                searchTerm === "" ||
                skill.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .sort((a, b) => {
              if (sortBy === "name") return a.name.localeCompare(b.name);
              if (sortBy === "lastName") return a.id - b.id; // Por número
              if (sortBy === "course") {
                // Por % completado
                const aCompleted = filteredStudents.filter(
                  (student) =>
                    getSkillLevel(student, amorDiosDimension.id, a.id) !== null
                ).length;
                const bCompleted = filteredStudents.filter(
                  (student) =>
                    getSkillLevel(student, amorDiosDimension.id, b.id) !== null
                ).length;
                const aPercentage =
                  filteredStudents.length > 0
                    ? (aCompleted / filteredStudents.length) * 100
                    : 0;
                const bPercentage =
                  filteredStudents.length > 0
                    ? (bCompleted / filteredStudents.length) * 100
                    : 0;
                return bPercentage - aPercentage;
              }
              return 0;
            })
            .map((skill) => {
              const completed = filteredStudents.filter(
                (student) =>
                  getSkillLevel(student, amorDiosDimension.id, skill.id) !==
                  null
              ).length;
              const percentage =
                filteredStudents.length > 0
                  ? (completed / filteredStudents.length) * 100
                  : 0;

              return (
                <button
                  key={skill.id}
                  onClick={() => setSelectedSkillId(skill.id)}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium text-left ${
                    getCompletionColor(percentage, selectedSkillId === skill.id)
                  }`}
                >
                  <div className="font-semibold" title={skill.name}>
                    {skill.name}
                  </div>
                  <div className={`text-xs mt-1 font-medium ${getPercentageTextColor(percentage, selectedSkillId === skill.id)}`}>
                    {percentage.toFixed(1)}% completado
                  </div>
                </button>
              );
            })}
        </div>

        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center flex-wrap">
              <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">
                  <span className="font-medium">≥75% completado,</span>
                </span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">
                  <span className="font-medium">≥50% completado,</span>
                </span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">
                  <span className="font-medium">≥25% completado,</span>
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">&lt;25% completado</span>
              </div>
            </div>
            <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
              Tip: Selecciona una habilidad para ver detalles
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderDataInputView = useMemo(() => {
    return () => (
      <div>
        {viewMode === "student" ? renderStudentView() : renderSkillView()}
      </div>
    );
  }, [viewMode, renderStudentView, renderSkillView]);

  return (
    <div className="space-y-4">
      <UnifiedHeader
        dimensionName={amorDiosDimension.name}
        icon={FaHeart}
        skillsCount={amorDiosDimension.skills.length}
        selectedCourse={selectedCourse}
        courses={courses}
        onCourseChange={setSelectedCourse}
      />

      {/* Controles de modo de vista */}
      <div className="mt-3">
        <div className="bg-gray-100 p-1 rounded-lg shadow-sm max-w-md">
          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => handleViewModeChange("student")}
              className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                viewMode === "student"
                  ? "bg-indigo-600 text-white shadow-md"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaUser className="mr-1.5 text-sm" />
              <span className="font-medium">Por Estudiante</span>
            </button>

            <button
              onClick={() => handleViewModeChange("skill")}
              className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                viewMode === "skill"
                  ? "bg-indigo-600 text-white shadow-md"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaList className="mr-1.5 text-sm" />
              <span className="font-medium">Por Habilidad</span>
            </button>
          </div>
        </div>
      </div>

      {renderSelectionButtons()}

      <div className="bg-white p-4 rounded-lg shadow-md">
        {renderDataInputView()}

        {/* Leyenda de colores para los niveles de las tablas */}
        {((viewMode === "student" && selectedStudentIds.length > 0) ||
          (viewMode === "skill" && selectedSkillId !== null)) && (
          <UnifiedLegend />
        )}
      </div>
    </div>
  );
};

export default AmorDios;
